/**
 * #3266 SYNTHACCESS: the server-side agents that run on a synthetic access
 * instead of a user's role (the #587 troubleshooting agent, the #1835 WhatsApp
 * responder) and the ONLY tools each may see or execute.
 *
 * Default deny. A synthetic access carries `synthetic_agent`, and
 * `isToolAllowed` (shared/rbac.ts) then answers from THIS table alone: no
 * SELF_CONTENT tools (attachments, reports, access requests, the caller's
 * Outlook mailbox), no run_sql-derived tools, no "*" in allowed_tools. The
 * chat tool assembly skips the Outlook append for a synthetic access too
 * (routes/chat.ts visibleToolDefinitions), so the tool list the model is shown
 * and the tool list the executor allows are both this row.
 *
 * Adding a tool to an agent is a one-line change here, reviewed like a role
 * grant. tests/synthetic-agent-tools-3266.test.ts walks every agent against
 * this table through the real registry.
 */
import type { RoleDataAccess } from "./rbac";
import { codeDefinedGrants, exactTableGrant } from "./table-grants";

export const SYNTHETIC_AGENT_TOOLS = {
  /** #587: KB-only field answers for reps. */
  troubleshooting: ["search_kb", "get_product_info"],
  /**
   * #1835: products advice for a group chat, where every member reads every
   * reply. Product, specification and QA test data only: no Ostendo tools (raw
   * supplier prices, receipt and assembly costs), no material identity (its
   * live ItemMaster fallback is Ostendo), and run_sql reaches only the
   * `qa_production_` prefix (WHATSAPP_TABLE_PREFIXES).
   */
  whatsapp: [
    "search_kb",
    "get_product_info",
    "get_spec_limits",
    "get_recent_failures",
    "count_tests",
    "calculate",
    "calculate_process_stats",
    "list_tables",
    "get_table_schema",
    "run_sql",
  ],
  /**
   * #1835 slice 12 WAGROUPS, QC Desk: today's `whatsapp` row, unchanged. A
   * group mapped to `whatsapp` (the legacy single conversation included) and
   * one mapped to `whatsapp_qc` run on the same tools and prefixes.
   */
  whatsapp_qc: [
    "search_kb",
    "get_product_info",
    "get_spec_limits",
    "get_recent_failures",
    "count_tests",
    "calculate",
    "calculate_process_stats",
    "list_tables",
    "get_table_schema",
    "run_sql",
  ],
  /**
   * #1835 slice 12, Products & Tech: the knowledge base, the catalogue, the
   * published and internal TDS view, spec limits and competitor equivalence.
   * No SQL of any kind (so no cost table is reachable), no Ostendo, no
   * material identity or costs, no batch data.
   */
  whatsapp_products: [
    "search_kb",
    "get_product_info",
    "get_product_tds",
    "get_spec_limits",
    "compare_competitor_product",
    "calculate",
  ],
  /**
   * #1835 slice 12, Plant & Production: batches and QC trends. run_sql and the
   * table-gated analytics reach only WHATSAPP_PLANT_TABLE_PREFIXES, each proven
   * to hold no cost or price column (tests/whatsapp-groups-1835.test.ts scans
   * DATABASE_SCHEMA.md). #4308: under a synthetic profile get_production_sequence
   * takes its grant-limited path: production_monitor_batch alone, checked by
   * run_sql's gate, no product_name (that join runs through
   * production_monitor_formulation, outside this grant).
   */
  whatsapp_plant: [
    "search_kb",
    "get_production_sequence",
    "get_spec_limits",
    "get_recent_failures",
    "count_tests",
    "calculate",
    "calculate_process_stats",
    "trend_analysis",
    "distribution",
    "list_tables",
    "get_table_schema",
    "run_sql",
  ],
} as const satisfies Record<string, readonly string[]>;

export type SyntheticAgentName = keyof typeof SYNTHETIC_AGENT_TOOLS;

export const SYNTHETIC_AGENT_NAMES = Object.keys(SYNTHETIC_AGENT_TOOLS) as SyntheticAgentName[];

/** #1835: QA test and spec tables. Not production_monitor_ (material cost) or raw_materials_ (price_per_kg). */
export const WHATSAPP_TABLE_PREFIXES: readonly string[] = ["qa_production_"];

/**
 * #1835 slice 12: Plant & Production's SQL reach. The QC prefix plus three
 * EXACT tables (exactTableGrant, shared/rbac.ts), never a production_monitor_
 * prefix: a prefix would also reach production_monitor_batchmaterial (kg per
 * material per batch, which reveals formulations), production_monitor_material
 * (cost) and production_monitor_formulation* (recipes). The three tables hold
 * no monetary column. Never raw_materials_ or research_rawmaterial (price_per_kg).
 */
export const WHATSAPP_PLANT_TABLE_PREFIXES: readonly string[] = [
  "qa_production_",
  exactTableGrant("production_monitor_batch"),
  exactTableGrant("production_monitor_product"),
  exactTableGrant("production_monitor_planning"),
];

/** The agent names a whatsapp_groups row may carry. Anything else is never polled. */
export const WHATSAPP_GROUP_AGENTS = ["whatsapp", "whatsapp_qc", "whatsapp_products", "whatsapp_plant"] as const;
export type WhatsappGroupAgent = (typeof WHATSAPP_GROUP_AGENTS)[number];

/** run_sql reach per WhatsApp profile; a profile without run_sql gets none. */
export const WHATSAPP_AGENT_TABLE_PREFIXES: Readonly<Record<WhatsappGroupAgent, readonly string[]>> = {
  whatsapp: WHATSAPP_TABLE_PREFIXES,
  whatsapp_qc: WHATSAPP_TABLE_PREFIXES,
  whatsapp_products: [],
  whatsapp_plant: WHATSAPP_PLANT_TABLE_PREFIXES,
};

export function isWhatsappGroupAgent(name: unknown): name is WhatsappGroupAgent {
  return typeof name === "string" && (WHATSAPP_GROUP_AGENTS as readonly string[]).includes(name);
}

/** The access a whatsapp_groups row's agent runs on; null (never polled) for any other name. */
export function whatsappAgentAccess(name: unknown): RoleDataAccess | null {
  if (!isWhatsappGroupAgent(name)) return null;
  return syntheticAgentAccess(name, { allowed_table_prefixes: WHATSAPP_AGENT_TABLE_PREFIXES[name] });
}

/** Tools this agent may use; an unknown name gets none. */
export function syntheticAgentTools(name: string): readonly string[] {
  return Object.prototype.hasOwnProperty.call(SYNTHETIC_AGENT_TOOLS, name)
    ? SYNTHETIC_AGENT_TOOLS[name as SyntheticAgentName]
    : [];
}

/**
 * The access the runner hands callAI for a synthetic agent. Never stored in
 * D1, never unioned with a caller's roles. `allowed_table_prefixes` is always
 * explicit: run_sql's gate (sql-gate.ts) reads it, and an agent without SQL
 * gets none.
 */
export function syntheticAgentAccess(
  name: SyntheticAgentName,
  opts: { allowed_table_prefixes?: readonly string[] } = {},
): RoleDataAccess {
  return {
    synthetic_agent: name,
    allowed_tools: [...SYNTHETIC_AGENT_TOOLS[name]],
    // Minted, so exact grants ("=table") count here and only here (shared/table-grants.ts).
    allowed_table_prefixes: codeDefinedGrants(opts.allowed_table_prefixes ?? []),
    allowed_write_actions: [],
    data_restriction_note: "",
  };
}
